import { useMemo } from 'react'
import { type DocumentNode, useLazyQuery } from '@apollo/client'

import { useBrightspot } from '../../BrightspotProvider'
import mergeDocumentNode from '../../../utils/graphQL/mergeDocumentNode'
import { useBrightspotPage } from '../../../providers/BrightspotProviders/BrightspotPageProvider'

import ZION_CMS_PAGE_QUERY from '../../../pages/ZionCMSPage/ZionCmsPage.gql'
import ZION_CMS_EVENT_CALENDAR_PAGE_QUERY from '../../../pages/ZionCMSEventCalendarPage/ZionCMSEventCalendarPage.gql'
import ZION_CMS_EVENT_PAGE_QUERY from '../../../pages/ZionCMSEventPage/ZionCMSEventPage.gql'
import ZION_CMS_HELPCENTER_ARTICLE_QUERY from '../../../pages/ZionCMSHelpCenterArticle/ZionCMSHelpCenterArticle.gql'
import ZION_CMS_LESSON_PAGE_QUERY from '../../../pages/ZionCMSLessonPage/ZionCMSLessonPage.gql'
import ZION_CMS_LOCATION_PAGE_QUERY from '../../../pages/ZionCMSLocationPage/ZionCMSLocationPage.gql'
import ZION_CMS_SEARCH_PAGE_QUERY from '../../../pages/ZionCMSSearchPage/ZionCMSSearchPage.gql'
import ZION_CMS_SOP_PAGE_QUERY from '../../../pages/ZionCMSSOPPage/ZionCMSSOPPage.gql'
import ZION_CMS_AUTHOR_PAGE_QUERY from '../../../pages/ZionCMSAuthorPage/ZionCMSAuthorPage.gql'
import ZION_CMS_BLOG_POST_PAGE_QUERY from '../../../pages/ZionCMSBlogPostPage/ZionCMSBlogPostPage.gql'
import ZION_CMS_BLOG_PAGE_QUERY from '../../../pages/ZionCMSBlogPage/ZionCMSBlogPage.gql'
import ZION_CMS_TAG_PAGE_QUERY from '../../../pages/ZionCMSTagPage/ZionCMSTagPage.gql'

type PageResponse = {
  CustomPage?: ZionCMSPage
  EventCalendarPage?: ZionCMSEventCalendarPage
  EventPage?: ZionCMSEventPage
  HelpCenterArticle?: ZionCMSHelpCenterArticle
  HelpCenterSearch?: ZionCMSSearchPage
  LessonDetail?: ZionCMSLessonPage
  LocationPage?: ZionCMSLocationPage
  StandardOperatingProcedureDetail?: ZionCMSSOPPage
  AuthorPage?: ZionCMSAuthorPage
  BlogPostPage?: ZionCMSBlogPostDetail
  BlogDetail?: ZionCMSBlogPage
  TagPage?: ZionCMSTagPage
}

declare type ContentTypeMap = {
  contentType: string
  query: DocumentNode
  sitePostfix: string
  defaultPageSize?: number
  defaultPageIndex?: number
}

export const CONTENT_TYPE_QUERY_MAP: ContentTypeMap[] = [
  {
    contentType: 'Custom Page (ZionCMS)',
    query: ZION_CMS_PAGE_QUERY,
    sitePostfix: '',
  },
  {
    contentType: 'Internal Answer Article',
    query: ZION_CMS_HELPCENTER_ARTICLE_QUERY,
    sitePostfix: ':help-ctr',
  },
  {
    contentType: 'External Answer Article',
    query: ZION_CMS_HELPCENTER_ARTICLE_QUERY,
    sitePostfix: ':help-ctr',
  },
  {
    contentType: 'Temple Policy Article',
    query: ZION_CMS_HELPCENTER_ARTICLE_QUERY,
    sitePostfix: ':help-ctr',
  },
  {
    contentType: 'Site Search',
    query: ZION_CMS_SEARCH_PAGE_QUERY,
    sitePostfix: ':help-ctr',
  },
  {
    contentType: 'Standard Operating Procedure',
    query: ZION_CMS_SOP_PAGE_QUERY,
    sitePostfix: ':help-ctr',
  },
  {
    contentType: 'Lesson',
    query: ZION_CMS_LESSON_PAGE_QUERY,
    sitePostfix: ':help-ctr',
  },
  {
    contentType: 'Event',
    query: ZION_CMS_EVENT_PAGE_QUERY,
    sitePostfix: ':loc-evt-cal',
  },
  {
    contentType: 'Event Calendar',
    query: ZION_CMS_EVENT_CALENDAR_PAGE_QUERY,
    sitePostfix: ':loc-evt-cal',
  },
  {
    contentType: 'Location',
    query: ZION_CMS_LOCATION_PAGE_QUERY,
    sitePostfix: ':loc-evt-cal',
  },
  {
    contentType: 'Author',
    query: ZION_CMS_AUTHOR_PAGE_QUERY,
    sitePostfix: ':marketing',
    defaultPageSize: 12,
    defaultPageIndex: 0,
  },
  {
    contentType: 'Blog Post',
    query: ZION_CMS_BLOG_POST_PAGE_QUERY,
    sitePostfix: ':marketing',
  },
  {
    contentType: 'Blog',
    query: ZION_CMS_BLOG_PAGE_QUERY,
    sitePostfix: ':marketing',
  },
  {
    contentType: 'Tag',
    query: ZION_CMS_TAG_PAGE_QUERY,
    sitePostfix: ':marketing',
    defaultPageSize: 12,
    defaultPageIndex: 0,
  },
]

function getSearchParams(): { queryString?: string; pageCount?: string; pageIndex?: string } {
  const url = new URL(window.location.href)
  const queryString = url.searchParams.get('q') || undefined
  const pageCount = url.searchParams.get('limit') || undefined
  const pageIndex = url.searchParams.get('page') || undefined

  return { queryString, pageCount, pageIndex }
}

function useRequestVariables() {
  const { contentType, relativeUrl, previewId } = useBrightspotPage()
  const { queryString, pageCount, pageIndex } = getSearchParams()

  const typeMap = findQueryMapByContentType(contentType)
  const finalPageCount = pageCount ?? typeMap.defaultPageSize?.toString()
  const finalPageIndex = pageIndex ?? typeMap.defaultPageIndex?.toString()

  const pageRequest = finalPageCount && finalPageIndex ? { limit: +finalPageCount, page: +finalPageIndex } : undefined

  return {
    requestVariables: {
      id: previewId || relativeUrl || '/',
      queryString,
      pageRequest,
    },
  }
}

export default function useBrightspotQuery() {
  const { resolvedSite, contentType } = useBrightspotPage()
  const { requestVariables } = useRequestVariables()
  const { customPageQuery } = useBrightspot()
  // Here we need to get the right query for the contentType
  const typeMap = findQueryMapByContentType(contentType)
  const newQuery = useMemo(() => mergeDocumentNode(typeMap.query, customPageQuery), [customPageQuery, typeMap.query])

  const [executeQuery, { loading, error, data }] = useLazyQuery<PageResponse>(newQuery, {
    variables: requestVariables,
    context: { apiName: resolvedSite + typeMap.sitePostfix },
    fetchPolicy: 'cache-and-network',
  })

  return { executeQuery, loading, error, data }
}

function findQueryMapByContentType(contentType): ContentTypeMap {
  return CONTENT_TYPE_QUERY_MAP.find((entry) => entry.contentType === contentType) || CONTENT_TYPE_QUERY_MAP[0]
}
